<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { QueryClientProvider } from '@tanstack/svelte-query';
  import { queryClient } from './lib/api/query-client.js';
  import { session } from './lib/stores/session.svelte.js';
  import { router } from './lib/router/route.svelte.js';
  import { http } from './lib/api/http.js';
  import { startEmbedBootstrap } from './lib/embed/bootstrap.js';
  import { setBridge } from './lib/embed/bridge-singleton.svelte.js';
  import { realtimeTicket } from './lib/api/queries.js';
  import { RealtimeClient, type RealtimeFrame, type RealtimeTicket } from '@platform/realtime-client';
  import { PresenceTracker, setPresence } from './lib/realtime/presence.svelte.js';
  import { setRealtimeSender } from './lib/realtime/sender-singleton.js';
  import { typing } from './lib/stores/typing.svelte.js';
  import { registerServiceWorker } from './lib/push/sw-registration.js';
  import { ensureSubscription } from './lib/push/push-subscription.js';
  import { playNotificationChime, shouldPlayChime } from './lib/notifications/sound.js';
  import { notificationPrefs } from './lib/stores/notification-prefs.svelte.js';
  import { connection } from './lib/realtime/connection.svelte.js';
  import ConnectionStatus from './components/ConnectionStatus.svelte';
  import ConversationsPage from './routes/ConversationsPage.svelte';
  import ThreadPage from './routes/ThreadPage.svelte';
  import LoginPage from './components/LoginPage.svelte';

  const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8080';
  http.setBaseUrl(apiBase);

  const defaultWsUrl = (import.meta.env.VITE_WS_URL as string | undefined) ?? deriveWsUrl(apiBase);

  // Standalone mode = not embedded in an iframe (widget.messengerapp.ge
  // direct visit). In that case, skip the postMessage SSO handshake and
  // render LoginPage instead.
  const standalone = typeof window !== 'undefined' && window.parent === window;

  let realtime: RealtimeClient | null = null;
  let presence: PresenceTracker | null = null;

  onMount(() => {
    router.start();
    if (standalone) {
      const restored = session.hydrate();
      if (restored?.sessionToken) http.setSessionToken(restored.sessionToken);
    } else {
      const handles = startEmbedBootstrap(apiBase);
      // Phase 4-6 / T30: expose the EmbedBridge as a module singleton so
      // CustomerContextPanel, LinkedEntitiesList, and ConversationHeader can
      // post openHostEntity / createHostEntityRequest without prop drilling.
      setBridge(handles.bridge);
    }
    // M5.7: drop out of any peer-viewer list when the parent tab goes
    // hidden so a backgrounded widget doesn't keep claiming "Levan is
    // viewing". Re-claim on return-to-foreground if a thread is open.
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    // M5.3.a — handle the SW's WIDGET_NOTIFICATION_CLICK postMessage so
    // clicking a system notification actually routes us to the target
    // thread, not just focuses the tab. The SW posts this from
    // notificationclick (see public/sw.js). Listening here (App-level)
    // rather than in a component instance survives route transitions
    // and is removed cleanly on destroy.
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMessage);
    }
  });

  onDestroy(() => {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    }
    presence?.dispose();
    setPresence(null);
    setRealtimeSender(null);
    presence = null;
    realtime?.close();
    realtime = null;
  });

  // React to session readiness: open the WS once authenticated.
  $effect(() => {
    if (!session.state.ready) return;
    if (realtime) return; // already connected
    realtime = new RealtimeClient({
      defaultWsUrl,
      ticketProvider: async (): Promise<RealtimeTicket> => {
        const ticket = await queryClient.fetchQuery(realtimeTicket());
        return { token: ticket.token, wsUrl: ticket.wsUrl || defaultWsUrl };
      },
      // Close-code cadence is the useful production signal — a cluster of
      // 1006s means something upstream of the gateway. The shared client
      // stays console-free, so the app decides where this goes.
      logger: (event, detail) => {
        // eslint-disable-next-line no-console
        console.info(`[realtime] ${event}`, detail);
      },
    });
    realtime.on(handleRealtimeFrame);
    // M5.7: construct the per-conversation viewer tracker once realtime is
    // up, expose it as the module singleton so ConversationHeader can read
    // the peer list, and re-announce the active conversation after every
    // socket open (initial + reconnect).
    const rt = realtime;
    presence = new PresenceTracker(
      (frame) => rt.send(frame),
      () => session.state.sso?.user?.id ?? null,
      // Scopes viewing frames to the operator's tenant, which is what the
      // gateway namespaces its viewer sets by. Without it a multi-tenant
      // operator's frames are ambiguous and get rejected.
      () => session.state.sso?.tenant?.id ?? null,
    );
    setPresence(presence);
    setRealtimeSender((p) => rt.send(p));
    // The socket dropping was previously invisible: the client retries in the
    // background, so a dead feed looked identical to a quiet inbox and the
    // operator kept triaging a list that had stopped updating.
    realtime.onClose(() => connection.markClosed());
    realtime.onError(() => connection.markError());

    realtime.onOpen(() => {
      connection.markOpen();
      presence?.resumeActive();
      // Close the gap between WS disconnect and reconnect: invalidate the
      // conversations list AND (if a thread is open) its messages so any
      // message.created/typing/etc. frames published while the socket was
      // down are picked up. Without this, the sidebar catches up via its
      // periodic refetch but the open thread sits stale until the
      // operator navigates away and back. Initial connect refetches
      // harmlessly — the queries are already warm from page load.
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      const activeId =
        router.state.name === 'thread' ? router.state.conversationId : null;
      if (activeId) {
        void queryClient.invalidateQueries({
          queryKey: ['conversations', activeId, 'messages'],
        });
      }
    });
    void realtime.connect();
  });

  // M5.7: route changes drive setActive — on a thread route we declare the
  // open conversation; on the list route we declare none. Runs after the
  // realtime $effect so `presence` is non-null by the time a user navigates.
  $effect(() => {
    if (!presence) return;
    const r = router.state;
    presence.setActive(r.name === 'thread' ? r.conversationId : null);
  });

  // M5.3: register the Service Worker + ensure a push subscription exists.
  // Runs once when session is ready. ensureSubscription is a no-op when
  // Notification.permission !== 'granted' (the toggle button is what
  // actually requests permission); this $effect just handles the
  // re-mount case where permission was already granted previously and
  // the browser may have rotated the endpoint silently.
  let pushBootstrapped = $state(false);
  $effect(() => {
    if (!session.state.ready || pushBootstrapped) return;
    pushBootstrapped = true;
    void (async () => {
      // M5.6.5 — pull the operator's account-level prefs before anything
      // reads them. The chime gate below and the toggle chip both render
      // from cached values until this lands, then correct themselves.
      await notificationPrefs.load();
      const reg = await registerServiceWorker();
      if (!reg) return;
      try {
        await ensureSubscription(reg);
      } catch {
        // Push is best-effort; the operator can re-enable via the toggle.
      }
    })();
  });

  function handleRealtimeFrame(frame: RealtimeFrame) {
    const conversationId =
      typeof frame.payload?.conversationId === 'string' ? frame.payload.conversationId : null;
    switch (frame.eventName) {
      case 'message.created':
      case 'message.updated': {
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        if (conversationId) {
          void queryClient.invalidateQueries({
            queryKey: ['conversations', conversationId, 'messages'],
          });
          // Phase 8.1 / M8.1.8 — the messages-service inbound consumer
          // clears `ai_summary_at_message_id` on every new INBOUND
          // (M8.1.5), so the server treats the next read as a cache
          // miss and regenerates. Invalidate the widget query so the
          // banner picks up the fresh copy instead of waiting for
          // staleTime. We invalidate unconditionally (without inspecting
          // direction): the query is `enabled: assignee === me`, so
          // non-assignees never refetch; the outbound-message case
          // costs one cache-hit round-trip per operator send, which is
          // acceptable for code-simplicity.
          void queryClient.invalidateQueries({
            queryKey: ['conversations', conversationId, 'ai-summary'],
          });
          // A real message arriving means the customer is no longer "still
          // typing" — clear the indicator immediately so the pill doesn't
          // dangle alongside the new bubble.
          typing.clear(conversationId);
          // M5.2 — in-app chime on new INBOUND, gated on "not currently
          // viewing this conversation" and the local sound preference.
          // shouldPlayChime is the pure predicate; playNotificationChime
          // never throws (graceful degrade when Web Audio is locked).
          if (
            shouldPlayChime({
              frame,
              activeConversationId:
                router.state.name === 'thread' ? router.state.conversationId : null,
              soundEnabled: notificationPrefs.state.soundEnabled,
            })
          ) {
            void playNotificationChime();
          }
          // M5.1 — if this is the open thread, the operator is actively
          // reading; advance the read-cursor so the unread chip doesn't
          // briefly tick up before the next list refetch clears it. POSTed
          // directly (not through TanStack) — the server's own
          // `conversation.read.updated` event will invalidate the list.
          const activeId =
            router.state.name === 'thread' ? router.state.conversationId : null;
          if (activeId === conversationId && frame.eventName === 'message.created') {
            void http
              .post(`/api/conversations/${conversationId}/mark-read`)
              .catch(() => {
                // Best-effort; the ThreadPage $effect re-fires on next
                // mount and the list refetch is already queued above.
              });
          }
        }
        break;
      }
      case 'conversation.typing': {
        // Diagnostic: temporarily log every typing frame so we can confirm
        // they reach the operator. Remove once W1 widget typing is verified.
        // eslint-disable-next-line no-console
        console.log('[typing-recv]', {
          conversationId,
          on: frame.payload?.on,
          fromVisitor: (frame.payload as { fromVisitor?: boolean })?.fromVisitor,
        });
        if (!conversationId) break;
        const on = frame.payload?.on === true;
        typing.set(conversationId, on);
        break;
      }
      case 'message.reaction-updated': {
        // Phase 3: customer reacted (or unreacted) on an operator/page
        // message. messages-service has already patched the row; we just
        // invalidate the per-conversation messages query so the bubble
        // re-renders with the new `reactions` field.
        if (conversationId) {
          void queryClient.invalidateQueries({
            queryKey: ['conversations', conversationId, 'messages'],
          });
        }
        break;
      }
      case 'conversation.updated': {
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        // Phase 4-6 / T29: a conversation.updated frame may carry a fresh
        // host-summary (match worker landed, summary refreshed, etc.). Drop
        // the cached host-summary for this conversation so the panel refetches
        // on next read.
        if (conversationId) {
          void queryClient.invalidateQueries({
            queryKey: ['conversations', conversationId, 'host-summary'],
          });
        }
        break;
      }
      case 'conversation.viewing.updated': {
        // M5.7 — viewer-presence broadcast. PresenceTracker decides whether
        // to apply (matches active conv) or discard (stale switch).
        presence?.handleViewingUpdated(frame.payload);
        break;
      }
      case 'conversation.tag.updated': {
        // G1.6 — peer applied or removed a tag. Invalidate conversations
        // so the chip strip on the affected row + the header pill both
        // refresh. The host-summary cache is independent; no invalidation
        // needed there.
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        break;
      }
      case 'conversation.assignee.updated': {
        // Assignee changed — the operator picker, or this widget's own
        // assign/release button. Nothing releases a conversation on its own
        // any more (the TTL and its sweeper went on 2026-09-07), so every
        // event here was caused by a person. The row's assignee chip, the
        // header button and the Owner filter all need fresh data.
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        // Phase 8.1 / M8.1.8 — claim flipped (to me, to someone else,
        // or released). When it flips TO me, ConversationSummary's
        // `enabled` derives true on the next render and the query
        // fires for the first time; invalidating up-front means it
        // refetches immediately instead of waiting for the operator
        // to revisit the thread after the list refetch lands.
        // The flip-away case is implicit: the query becomes disabled,
        // invalidation is a no-op, and the cached summary stays put
        // for next re-claim's instant render.
        if (conversationId) {
          void queryClient.invalidateQueries({
            queryKey: ['conversations', conversationId, 'ai-summary'],
          });
        }
        break;
      }
      case 'conversation.read.updated': {
        // M5.1 — this operator's read cursor advanced (from this tab or
        // another tab they have open). Invalidate so the unread chip
        // drops to zero across tabs. Server only fans this event to the
        // affected userId, so no need to filter further.
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        break;
      }
      case 'conversation.note.created':
      case 'conversation.note.deleted': {
        // G2.3 — peer added or removed an internal note. Invalidate the
        // notes cache for the affected conversation so the panel
        // refetches; also the conversations list because the server
        // touches conv updatedAt on note writes (row resurfaces).
        if (conversationId) {
          void queryClient.invalidateQueries({
            queryKey: ['conversations', conversationId, 'notes'],
          });
        }
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        break;
      }
      case 'host-summary.updated': {
        // Optional event — backend may emit this from the host-match worker.
        // Plan T29 says polling via 60s staleTime is sufficient, but we still
        // honor an explicit invalidation if it arrives.
        if (conversationId) {
          void queryClient.invalidateQueries({
            queryKey: ['conversations', conversationId, 'host-summary'],
          });
        }
        break;
      }
      default:
        // Unknown event names are intentionally ignored; future event names
        // can be wired here without touching the WS service itself.
        break;
    }
  }

  // M5.3.a — SW notification-click handler. The SW posts
  // { type: 'WIDGET_NOTIFICATION_CLICK', data: { eventName, conversationId, ts } }
  // when the operator clicks a system notification. Route to the target
  // thread, but only if the notification is recent (5 min) — an old
  // notification clicked the next morning shouldn't hijack the operator
  // out of whatever they're currently looking at.
  //
  // Bound as a function reference so onDestroy can remove the same
  // listener cleanly.
  const STALE_NOTIFICATION_MS = 5 * 60 * 1000;
  function onSwMessage(event: MessageEvent): void {
    const data = event.data as { type?: unknown; data?: unknown } | null;
    if (!data || data.type !== 'WIDGET_NOTIFICATION_CLICK') return;
    const payload = data.data as { conversationId?: unknown; ts?: unknown } | null;
    if (!payload || typeof payload.conversationId !== 'string') return;
    const ts = typeof payload.ts === 'string' ? Date.parse(payload.ts) : NaN;
    // Stale-guard: if ts is missing or older than the window, ignore.
    // Routing anyway would teleport the operator mid-task with no
    // visible cause; better to silently no-op and let them navigate
    // manually if they still care about that conversation.
    if (Number.isNaN(ts) || Date.now() - ts > STALE_NOTIFICATION_MS) return;
    router.navigateToThread(payload.conversationId);
  }

  // M5.7 visibility handler — arrow so the bound reference survives
  // add/removeEventListener round-trips. On hide: setActive(null) tells
  // gateway we left. On show: setActive(currentConvId) re-claims if still
  // on a thread route.
  function onVisibilityChange(): void {
    if (!presence) return;
    if (document.visibilityState === 'hidden') {
      presence.setActive(null);
      return;
    }
    if (document.visibilityState === 'visible') {
      const r = router.state;
      presence.setActive(r.name === 'thread' ? r.conversationId : null);
    }
  }

  function deriveWsUrl(httpBase: string): string {
    try {
      const u = new URL(httpBase);
      const proto = u.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${u.host}/api/realtime/ws`;
    } catch {
      return 'ws://localhost:8080/api/realtime/ws';
    }
  }
</script>

<QueryClientProvider client={queryClient}>
  <main class="widget-root">
    {#if session.state.error}
      <div class="error">
        <p class="heading">Could not connect</p>
        <p class="sub">{session.state.error}</p>
      </div>
    {:else if !session.state.ready && standalone}
      <LoginPage />
    {:else if !session.state.ready}
      <div class="loading">Authenticating...</div>
    {:else if router.state.name === 'list'}
      <!-- Above the list, not inside it: the warning is about everything the
           operator can see, so it must not scroll away with the rows. -->
      <ConnectionStatus />
      <ConversationsPage />
    {:else}
      <ConnectionStatus />
      <ThreadPage conversationId={router.state.conversationId} />
    {/if}
  </main>
</QueryClientProvider>

<style>
  /*
   * Brushed Steel + Signal Amber tokens (see DESIGN.md). Defaults are declared
   * here so every descendant inherits them; the host CRM iframe embedder may
   * override any of them via setProperty in apply.ts.
   */
  :global(:root) {
    --widget-bg: oklch(98% 0.005 250);
    --widget-bg-elevated: oklch(99.5% 0.003 250);
    --widget-bg-sunk: oklch(96% 0.006 250);
    --widget-fg: oklch(20% 0.012 250);
    --widget-fg-muted: oklch(48% 0.012 250);
    --widget-fg-dim: oklch(64% 0.010 250);
    --widget-border: oklch(88% 0.008 250);
    --widget-divider: oklch(93% 0.006 250);
    --widget-accent: oklch(75% 0.165 75);
    --widget-accent-deep: oklch(50% 0.19 65);
    --widget-accent-tint: oklch(94% 0.05 80);
    --widget-danger: oklch(55% 0.20 25);
    --widget-danger-tint: oklch(94% 0.04 25);
    --widget-channel-fb: oklch(58% 0.18 260);
    --widget-channel-ig: oklch(60% 0.22 0);
    --widget-channel-wa: oklch(63% 0.17 145);
    --widget-radius: 8px;
    --widget-radius-action: 5px;
    --widget-font:
      system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial,
      sans-serif;
    --widget-font-mono: ui-monospace, 'SF Mono', 'JetBrains Mono', Menlo, monospace;
  }
  :global(html, body, #app) {
    height: 100%;
    margin: 0;
  }
  :global(body) {
    font-family: var(--widget-font);
    -webkit-font-smoothing: antialiased;
    background: var(--widget-bg);
    color: var(--widget-fg);
  }

  /* M5.8 Slice C — vestibular safety. Honor the OS-level
     prefers-reduced-motion setting site-wide instead of patching each
     component's animation/transition individually (toast slide-in, panel
     slide, typing pill fade, header transitions, etc.). 1ms instead of 0
     so opacity/state transitions still fire their `transitionend` events
     where any script depends on them — only the *motion* is removed. */
  @media (prefers-reduced-motion: reduce) {
    :global(*),
    :global(*::before),
    :global(*::after) {
      animation-duration: 1ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 1ms !important;
      scroll-behavior: auto !important;
    }
  }
  .widget-root {
    height: 100%;
    display: flex;
    flex-direction: column;
    color: var(--widget-fg);
    background: var(--widget-bg);
  }
  .error {
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
  }
  .error .heading {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--widget-danger);
  }
  .error .sub {
    margin: 0;
    font-size: 12.5px;
    color: var(--widget-fg-muted);
    max-width: 40ch;
  }
  .loading {
    padding: 24px;
    text-align: center;
    color: var(--widget-fg-muted);
    font-size: 10.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 700;
  }
</style>
